import { Section } from '../../components/ExamViewer/types';
import { Question } from '../../services/exams/types';
import { QuestionType } from '../../types/common';

// Create section label function
const getSectionLabel = (questionType: string, firstLabel: string, lastLabel?: string) => {
  if (questionType === QuestionType.MultipleChoice) {
    return (firstLabel === lastLabel || !lastLabel) ? `MCQ ${firstLabel}` : `MCQ ${firstLabel} - ${lastLabel}`;
  }
  return `Question ${firstLabel}`;
};

const getNewSection = (questionType: string, questionLabel: string) => ({
  sectionLabel: getSectionLabel(questionType, questionLabel),
  questions: [],
});

// Splits questions into sections based on the question type and label
// If the question type is MCQ, then section contains consecutive MCQ questions up to
// maxDisplayedMCQ or the next non-MCQ question
// If the question type is not MCQ, then section contains questions with the same first number
// in the label
// This function also creates the question label for each section
export const getQuestionSections = (questions: Question[], maxDisplayedMCQ: number = 20) => {
  const sections: Section[] = [];
  let currentSection: Section = { sectionLabel: '', questions: [] };
  let firstQLabel = '';
  let lastQLabel = '';

  const addSectionToList = (section: Section, isMultipleChoice: boolean) => {
    if (isMultipleChoice) {
      section.sectionLabel = getSectionLabel(QuestionType.MultipleChoice, firstQLabel, lastQLabel);
    }
    sections.push(section);
  };

  questions.forEach((question) => {
    const questionType = question.type;
    const questionLabel = question.label.split('#')[0];

    if (currentSection.questions.length === 0) {
      currentSection = getNewSection(questionType, questionLabel);
      firstQLabel = questionLabel;
    } else if (currentSection.questions.length === maxDisplayedMCQ ||
    (currentSection.questions[0]?.type === QuestionType.MultipleChoice
        && questionType !== QuestionType.MultipleChoice) ||
    (currentSection.questions[0]?.type !== QuestionType.MultipleChoice
        && questionLabel !== firstQLabel)
    ) {
      addSectionToList(
        currentSection, currentSection.questions[0]?.type === QuestionType.MultipleChoice,
      );
      currentSection = getNewSection(questionType, questionLabel);
      firstQLabel = questionLabel;
    }
    lastQLabel = questionLabel;

    currentSection.questions.push(question);
  });

  if (currentSection.questions.length > 0) {
    addSectionToList(
      currentSection, currentSection.questions[0]?.type === QuestionType.MultipleChoice,
    );
  }

  return sections;
};

// Formats label such as 3 to 3 and 3#a to 3(a) and 3#a#i to 3(a)(i)
export const formatQuestionLabel = (label: string) => {
  const labelParts = label.split('#');
  return labelParts.reduce((acc, part, index) => {
    if (index === 0) {
      return part;
    }
    return `${acc}(${part})`;
  }, '');
};

// Returns array of errorTypes given the mark, maxMark and errorType
// For example mark of 1, maxMark of 3 and error type of 'SILLY'
// Will return ['CORRECT', 'SILLY', 'SILLY']
export const getMarksBreakdown = (mark: number, maxMark: number, errorType?: string) => {
  const marksBreakdown = [];
  for (let i = 0; i < mark; i += 1) {
    marksBreakdown.push('CORRECT');
  }
  for (let i = mark; i < maxMark; i += 1) {
    if (!errorType) return undefined;
    // the default option should be removed when the marking system is
    // updated to be on a per-mark basis
    marksBreakdown.push(errorType);
  }
  return marksBreakdown;
};

export const getErrorType = (marksBreakdown: string[] | undefined) => {
  if (!marksBreakdown) return undefined;
  return marksBreakdown.find((mark: string) => mark !== 'CORRECT');
};
